'use client'

import { User, ChevronLeft } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTypewriter, Cursor } from 'react-simple-typewriter'
import { ReactNode, useState, useEffect } from 'react'
import { ChatAvatar } from '../icons';
import { Button } from '../buttons'

/**
 * Props for the `ChatFormSection` component.
 */
interface ChatFormSectionProps {
  /**
   * Message(s) to be typed out by the assistant.
   * Can be a string or an array of string sections.
   */
  assistantMessage: string | string[]

  /**
   * Optional content to show after the assistant finishes typing.
   */
  children?: ReactNode

  /**
   * Whether the response section should be shown after typing is complete.
   * @default true
   */
  showResponse?: boolean

  /**
   * Callback fired after the typewriter animation completes.
   */
  onAnimationComplete?: () => void

  /**
   * Optional callback when the back button is pressed.
   */
  onBack?: () => void

  /**
   * Whether to show a back button next to the avatar.
   * @default false
   */
  showBackButton?: boolean

  /**
   * Optional custom avatar settings.
   */
  avatar?: {
    /**
     * Icon element for the avatar.
     */
    icon?: ReactNode

    /**
     * Display name for the avatar.
     */
    name?: string

    /**
     * Avatar size.
     * @default "lg"
     */
    size?: 'sm' | 'md' | 'lg'
  }

  /**
   * Additional CSS class names for the outer container.
   */
  className?: string
}

/**
 * `ChatFormSection` displays an assistant message with typewriter animation,
 * an avatar, optional back button, and optional user response content that fades in after typing.
 *
 * @example
 * ```tsx
 * <ChatFormSection assistantMessage="Hello! How can I help?">
 *   <YourFormComponent />
 * </ChatFormSection>
 * ```
 */
export function ChatFormSection({
  assistantMessage,
  children,
  showResponse: initialShowResponse = true,
  onAnimationComplete,
  onBack,
  showBackButton = false,
  avatar = {
    icon: <User />,
    name: 'Assistant',
    size: 'lg',
  },
  className,
}: ChatFormSectionProps) {
  const [showCursor, setShowCursor] = useState(true)
  const [isTypingDone, setIsTypingDone] = useState(false)

  const fullMessage = Array.isArray(assistantMessage)
    ? assistantMessage.join('\n\n')
    : assistantMessage

  const [text] = useTypewriter({
    words: [fullMessage],
    typeSpeed: 5,
    delaySpeed: 0,
    deleteSpeed: 0,
    loop: 1,
  })

  useEffect(() => {
    if (text === fullMessage) {
      setShowCursor(false)
      setIsTypingDone(true)
      onAnimationComplete?.()
    }
  }, [text, fullMessage, onAnimationComplete])

  return (
    <div className={`chat-message ${className || ''}`}>
      <div className="flex items-start gap-4">
        {showBackButton && (
          <Button variant="ghost" size="icon" onClick={onBack} className="mt-1">
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}

        <div className="flex-shrink-0 mt-1">
          <ChatAvatar
            icon={avatar.icon}
            name={avatar.name}
            size={avatar.size}
          />
        </div>

        <div className="flex-1 space-y-4 max-w-[80%]">
          <div className="assistant-message text-base">
            <p style={{ whiteSpace: 'pre-wrap' }}>
              {text}
              {showCursor && <Cursor />}
            </p>
          </div>

          <div className="flex justify-center">
            <AnimatePresence>
              {isTypingDone && initialShowResponse && children && (
                <motion.div
                  className="user-response w-full max-w-3xl"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  {children}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <style jsx>{`
        .chat-message {
          max-width: 100%;
          margin-bottom: 2rem;
          width: 100%;
        }

        .assistant-message {
          background-color: #f3f4f6;
          border-radius: 1.25rem;
          padding: 1.5rem;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .user-response {
          margin-top: 1rem;
          padding-left: 0.5rem;
        }

        .user-response :global(.flex:has(button)) {
          justify-content: center;
        }

        .user-response :global(.flex:has(button) > *) {
          margin-left: auto;
          margin-right: auto;
        }

        @media (max-width: 640px) {
          .user-response {
            padding-left: 0;
          }
        }
      `}</style>
    </div>
  )
}
